import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import ToolTip from 'core/components/Tooltip';
import isNil from 'lodash/isNil';
import {
  formatPhone,
  notEmptyString,
} from '@populateui/utils';
import {
  formatDate,
} from 'utils/date';
import clsx from 'clsx';
import {
  Button,
} from 'pages/Dashboard/components/Button';
import Textarea from 'pages/Dashboard/components/Input/Textarea';
import Spinner from 'pages/Dashboard/components/Spinner';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import {
  exportToCsv,
  exportToPdf,
} from 'views/EMR/PopulateSupport/tableExport';
import {
  usePopulateSupportGenerateSql,
  usePopulateSupportExecuteSql,
} from 'views/EMR/PopulateSupport/useSupportSqlHooks';
import {
  SupportSqlExecutionResult,
  SupportSqlGenerationResult,
  SupportSqlPromptSuggestion,
  SupportSqlResultColumn,
} from 'dtos';
import SupportSqlPromptRecoverySection from 'views/EMR/PopulateSupport/SupportSqlPromptRecoverySection';

type QueryResultRow = Record<string, unknown>;

function getMessageFromError(error: any): string {
  const responseData = error?.response?.data;
  if (typeof responseData === 'string' && responseData.trim().length > 0) {
    return responseData;
  }
  if (notEmptyString(responseData?.message)) {
    return responseData.message;
  }
  if (notEmptyString(responseData?.Message)) {
    return responseData.Message;
  }
  if (notEmptyString(error?.message)) {
    return error.message;
  }
  return 'Something went wrong. Please try again.';
}

const DATE_SQL_TYPES = new Set(['date', 'datetime', 'datetime2', 'smalldatetime', 'datetimeoffset', 'timestamp']);
const DATE_COLUMN_PATTERN = /date|dob|birth/i;
const PHONE_COLUMN_PATTERN = /phone|fax|mobile/i;

function formatColumnValue(column: SupportSqlResultColumn, value: unknown): string {
  if (isNil(value)) return '--';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);

  const strValue = String(value);
  const dataType = (column.dataType ?? '').toLowerCase();
  const name = (column.name ?? '').toLowerCase();

  if (DATE_SQL_TYPES.has(dataType) || DATE_COLUMN_PATTERN.test(name)) {
    return formatDate(strValue) || strValue;
  }
  if (PHONE_COLUMN_PATTERN.test(name)) {
    return formatPhone(strValue);
  }
  return strValue;
}

function formatDuration(durationMs: number | null | undefined): string {
  if (isNil(durationMs)) return '--';
  if (durationMs < 1000) return `${durationMs} ms`;
  return `${(durationMs / 1000).toFixed(2)} s`;
}

type SupportSqlRecoveryResult =
  | SupportSqlGenerationResult
  | SupportSqlExecutionResult
  | null
  | undefined;

function hasUsablePromptSuggestion(suggestion?: SupportSqlPromptSuggestion | null): boolean {
  return notEmptyString(suggestion?.suggestedPrompt);
}

function shouldShowPromptRecovery(result?: SupportSqlRecoveryResult): boolean {
  if (!result?.shouldSuggestPromptImprovements) {
    return false;
  }

  return (result.promptSuggestions ?? [])
    .some(hasUsablePromptSuggestion);
}

type ResultCellProps = {
  value: string;
};

function ResultCell({ value }: ResultCellProps) {
  const ref = useRef<HTMLSpanElement>(null);
  return (
    <ToolTip title={value} auto ref={ref}>
      <span ref={ref} className="text-xs text-slate-700 truncate">
        {value}
      </span>
    </ToolTip>
  );
}

function getColumnName(column: SupportSqlResultColumn, index: number): string {
  return column.name ?? `column_${index}`;
}

const COLUMN_TITLE_OVERRIDES: Readonly<Record<string, string>> = Object.freeze({
  phonenumberphonenumberwithareacodevalue: 'Phone Number',
  genderdescription: 'Gender',
});

export function formatColumnTitle(name: string): string {
  const normalizedKey = name.toLowerCase().replace(/_/g, '');
  if (COLUMN_TITLE_OVERRIDES[normalizedKey]) {
    return COLUMN_TITLE_OVERRIDES[normalizedKey];
  }
  return name
    .replace(/_/g, ' ')
    .replace(/\bvalue\b/gi, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

const COLUMN_CHAR_WIDTH = 8;
const COLUMN_MIN_WIDTH = 70;
const COLUMN_PADDING = 24;

const COLUMN_MIN_SIZE_OVERRIDES: Readonly<Record<string, number>> = Object.freeze({
  organizationid: 90,
  email: 180,
});

function buildResultColumns(columns: SupportSqlResultColumn[]): ColumnDef<QueryResultRow>[] {
  return columns.map((column, index) => {
    const name = getColumnName(column, index);
    const title = formatColumnTitle(name);
    const normalizedKey = name.toLowerCase().replace(/_/g, '');
    const minSize = COLUMN_MIN_SIZE_OVERRIDES[normalizedKey]
      ?? Math.max(COLUMN_MIN_WIDTH, title.length * COLUMN_CHAR_WIDTH + COLUMN_PADDING);
    return {
      id: name,
      accessorKey: name,
      columnTitle: title,
      minSize,
      className: 'overflow-hidden',
      meta: {
        autoSized: true,
      },
      cell: ({ row }) => <ResultCell value={formatColumnValue(column, row.original[name])} />,
    };
  });
}

function buildResultRows(columns: SupportSqlResultColumn[], rows: unknown[][]): QueryResultRow[] {
  return rows.map((row) => Object.fromEntries(
    columns.map((column, index) => [getColumnName(column, index), row[index]]),
  ));
}
const defaultRowLimit = 200;
const defaultTimeoutSeconds = 30;
const defaultMaxRows = 500;

export default function SupportPortalQueryExecutionSection() {
  const [query, setQuery] = useState('');
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<SupportSqlGenerationResult | null>(null);
  const [executed, setExecuted] = useState<SupportSqlExecutionResult | null>(null);
  const [hasRunAttempted, setHasRunAttempted] = useState(false);

  const reviewSectionRef = useRef<HTMLDivElement>(null);
  const resultsSectionRef = useRef<HTMLDivElement>(null);

  const {
    mutateAsync: generateSql,
    isPending: isGenerating,
  } = usePopulateSupportGenerateSql();

  const {
    mutateAsync: executeSql,
    isPending: isExecuting,
  } = usePopulateSupportExecuteSql();

  const isBusy = isGenerating || isExecuting;
  const trimmedQuery = query.trim();
  const canGenerate = trimmedQuery.length > 0 && !isBusy;
  const canRunQuery = notEmptyString(generated?.sql) && !isBusy;
  const hasState = notEmptyString(query)
    || !isNil(generated)
    || !isNil(executed)
    || hasRunAttempted;

  const resultColumns = useMemo<ColumnDef<QueryResultRow>[]>(
    () => buildResultColumns(executed?.columns ?? []),
    [executed?.columns],
  );

  const resultRows = useMemo<QueryResultRow[]>(
    () => buildResultRows(executed?.columns ?? [], executed?.rows ?? []),
    [executed?.columns, executed?.rows],
  );

  const clearExecutionState = useCallback(() => {
    setGenerateError(null);
    setExecuteError(null);
    setExecuted(null);
    setHasRunAttempted(false);
  }, []);

  const onQueryChange = useCallback((value: string) => {
    setQuery(value);
    clearExecutionState();
    setGenerated(null);
  }, [clearExecutionState]);

  const onReset = useCallback(() => {
    setQuery('');
    clearExecutionState();
    setGenerated(null);
  }, [clearExecutionState]);

  const onUsePromptSuggestion = useCallback((suggestedPrompt: string) => {
    setQuery(suggestedPrompt);
    clearExecutionState();
    setGenerated(null);
  }, [clearExecutionState]);

  const onGenerateSql = useCallback(async () => {
    if (!canGenerate) return;

    clearExecutionState();

    try {
      const response = await generateSql({
        requestText: trimmedQuery,
        rowLimit: defaultRowLimit,
      });
      setGenerated(response);

      if (notEmptyString(response.sql)) {
        setTimeout(() => {
          reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
      } else {
        setGenerateError('SQL generation succeeded, but no SQL text was returned.');
      }
    } catch (error) {
      setGenerated(null);
      setGenerateError(getMessageFromError(error));
    }
  }, [canGenerate, defaultRowLimit, generateSql, trimmedQuery]);

  const exportData = useMemo(() => {
    const columns = executed?.columns ?? [];
    const headers = buildResultColumns(columns).map(
      (column) => (column.columnTitle ?? column.id) as string,
    );
    const rows = (executed?.rows ?? []).map(
      (row) => columns.map((column, index) => formatColumnValue(column, row[index])),
    );
    return { headers, rows };
  }, [executed?.columns, executed?.rows]);

  const onExportCsv = useCallback(() => {
    exportToCsv(exportData.headers, exportData.rows);
  }, [exportData.headers, exportData.rows]);

  const onExportPdf = useCallback(() => {
    exportToPdf(exportData.headers, exportData.rows);
  }, [exportData.headers, exportData.rows]);

  const onRunQuery = useCallback(async () => {
    if (!canRunQuery || !generated) return;

    setExecuteError(null);
    setHasRunAttempted(true);

    try {
      const response = await executeSql({
        sql: generated.sql,
        timeoutSeconds: defaultTimeoutSeconds,
        maxRows: defaultMaxRows,
      });
      setExecuted(response);
      if (notEmptyString(response.error)) {
        setExecuteError(response.error ?? null);
      }
      setTimeout(() => {
        resultsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    } catch (error) {
      setExecuted(null);
      setExecuteError(getMessageFromError(error));
    }
  }, [canRunQuery, defaultMaxRows, defaultTimeoutSeconds, executeSql, generated]);

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 md:p-8">
        <div className="flex flex-col gap-2 mb-6">
          <h2 className="text-xl font-semibold text-slate-900">Query Execution</h2>
          <p className="text-sm text-slate-600">
            Write a natural-language request, generate SQL, review it carefully,
            then run it to inspect results.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <Textarea
            id="support-sql-natural-language-query"
            label="Natural Language Query"
            value={query}
            onChange={onQueryChange}
            rows={7}
            placeholder="Example: Show the last 20 denied claims for Acme Health in February 2026,
            including patient name, claim number, and denial reason."
            containerClassName="!border-slate-200 !rounded-lg !max-h-52"
            textAreaClassName="!text-sm !leading-6 !py-3 !px-3"
          />

          {notEmptyString(generateError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              {generateError}
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              variant="contained"
              color="primary"
              disabled={!canGenerate}
              onClick={onGenerateSql}
              className={clsx(
                '!bg-slate-900 hover:!bg-slate-800 !text-white !px-4 !py-2 !rounded-lg flex items-center gap-2',
                { 'disabled:!bg-slate-200 disabled:!text-slate-400 !cursor-not-allowed !pointer-events-auto': !canGenerate },
              )}
            >
              {isGenerating ? <Spinner className="!text-white" size={16} /> : <AutoAwesomeRoundedIcon className="!w-5 !h-5" />}
              {generated ? 'Regenerate SQL' : 'Generate SQL'}
            </Button>

            {hasState && (
              <Button
                variant="text"
                color="primary"
                disabled={isBusy}
                onClick={onReset}
                className="!px-2 !py-1 !text-slate-700 hover:!bg-slate-100"
              >
                <RestartAltRoundedIcon className="!w-5 !h-5" />
                Start Over
              </Button>
            )}
          </div>
        </div>
      </div>

      {!isNil(generated) && (
        <div ref={reviewSectionRef} className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 md:p-8">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold text-slate-900">Review Generated Query</h3>
              <p className="text-sm text-slate-600">
                Verify the SQL and notes before executing against production data.
              </p>
            </div>
            <Button
              variant="contained"
              color="primary"
              disabled={!canRunQuery}
              onClick={onRunQuery}
              className={clsx(
                '!bg-amber-500 hover:!bg-amber-600 !text-slate-900 !font-semibold !px-4 !py-2 !rounded-lg flex items-center gap-2',
                { 'disabled:!bg-slate-200 disabled:!text-slate-400 !cursor-not-allowed !pointer-events-auto': !canRunQuery },
              )}
            >
              {isExecuting ? <Spinner className="!text-slate-900" size={16} /> : <PlayArrowRoundedIcon className="!w-5 !h-5" />}
              Run Query
            </Button>
          </div>

          {notEmptyString(generated.summary) && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Summary
              </p>
              <p className="text-sm text-slate-700 leading-6">{generated.summary}</p>
            </div>
          )}

          {(generated.usedObjects ?? []).length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Used Objects
              </p>
              <div className="flex flex-wrap gap-2">
                {(generated.usedObjects ?? []).map((item) => (
                  <span
                    key={item}
                    className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(generated.warnings ?? []).length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">
                Warnings
              </p>
              <ul className="list-disc list-inside text-sm text-amber-800 space-y-1">
                {(generated.warnings ?? []).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {(generated.unsupportedRequirements ?? []).length > 0 && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-700 mb-2">
                Unsupported Requirements
              </p>
              <ul className="list-disc list-inside text-sm text-rose-800 space-y-1">
                {(generated.unsupportedRequirements ?? []).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {shouldShowPromptRecovery(generated) && (
            <div className="mb-4">
              <SupportSqlPromptRecoverySection
                reason={generated?.promptImprovementReason}
                triggerStage={generated?.promptImprovementTriggerStage}
                suggestions={generated?.promptSuggestions}
                onUseSuggestion={onUsePromptSuggestion}
              />
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Generated SQL
            </p>
            <pre className={clsx(
              'rounded-lg border border-gray-400 bg-gray-100 text-gray-700 text-xs md:text-sm p-4 overflow-auto max-h-[360px]',
              { 'opacity-70': !notEmptyString(generated.sql) },
            )}
            >
              {notEmptyString(generated.sql) ? generated.sql : 'No SQL generated.'}
            </pre>
          </div>
        </div>
      )}

      {(hasRunAttempted || !isNil(executed)) && (
        <div ref={resultsSectionRef} className="bg-white rounded-lg shadow-sm border border-slate-200 pt-6">
          <div className="flex items-start justify-between gap-4 mb-4 px-6">
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold text-slate-900">Results</h3>
              <p className="text-sm text-slate-600">
                Review query output details and returned records.
              </p>
            </div>
            {resultRows.length > 0 && (
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={exportData.headers.length === 0}
                  onClick={onExportCsv}
                  className="!border-slate-300 !text-slate-600 hover:!border-slate-900 hover:!text-slate-900 hover:!bg-slate-100 !px-3 !py-1.5 !text-xs !rounded-lg flex items-center gap-1.5 !transition-colors"
                >
                  <FileDownloadRoundedIcon className="!w-4 !h-4" />
                  CSV
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={exportData.headers.length === 0}
                  onClick={onExportPdf}
                  className="!border-slate-300 !text-slate-600 hover:!border-slate-900 hover:!text-slate-900 hover:!bg-slate-100 !px-3 !py-1.5 !text-xs !rounded-lg flex items-center gap-1.5 !transition-colors"
                >
                  <PictureAsPdfRoundedIcon className="!w-4 !h-4" />
                  PDF
                </Button>
              </div>
            )}
          </div>

          {isExecuting && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 flex items-center gap-3 text-sm text-slate-600 px-6">
              <Spinner size={18} />
              Running query...
            </div>
          )}

          {!isExecuting && notEmptyString(executeError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 px-6">
              {executeError}
            </div>
          )}

          {!isExecuting && !notEmptyString(executeError) && !isNil(executed) && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 px-6">
                <span className="inline-flex rounded-full bg-slate-100 px-3 py-1">
                  Rows:
                  {' '}
                  {executed.rowCount}
                </span>
                <span className="inline-flex rounded-full bg-slate-100 py-1 px-3">
                  Duration:
                  {' '}
                  {formatDuration(executed.durationMs)}
                </span>
                {executed.truncated && (
                  <span className="inline-flex rounded-full bg-amber-100 py-1 text-amber-800">
                    Results truncated
                  </span>
                )}
              </div>

              {shouldShowPromptRecovery(executed) && (
                <div className="px-6">
                  <SupportSqlPromptRecoverySection
                    reason={executed?.promptImprovementReason}
                    triggerStage={executed?.promptImprovementTriggerStage}
                    suggestions={executed?.promptSuggestions}
                    onUseSuggestion={onUsePromptSuggestion}
                  />
                </div>
              )}

              {resultRows.length === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600 px-6">
                  Query executed successfully, but no rows were returned.
                </div>
              ) : (
                <div className="relative h-[calc(100vh-250px)] overflow-hidden">
                  <Table<QueryResultRow>
                    data={resultRows}
                    columns={resultColumns}
                    getRowId={(_, index) => index}
                    emptySlot="No results found"
                    classes={{
                      header: 'px-4',
                      scrollContainer: 'justify-center items-center !pb-0',
                      outerRow: () => '!w-[calc(100%-32px)] left-4 hover:border-[#dde2ee]',
                      row: () => '!items-center py-2 hover:rounded-md',
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
